"use client";

import TableSortingIcon from "@/components/common/TableSortingIcon";
import { ProductVariantsPopover } from "@/components/products/ProductVariantsPopover";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { getAsset } from "@team-shiphouse/shiphouse-sdk";
import { formatPaise } from "@team-shiphouse/shiphouse-sdk/client";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { InputPrimary } from "@/components/common/InputPrimary";
import { ButtonPrimary } from "@/components/common/ButtonPrimary";
import { ButtonOutline } from "@/components/common/ButtonOutline";
import { Label } from "@/components/ui/label";
import { Product, Metafield, VariantApiShape } from "@/types/models/product";
import { ColumnDef } from "@tanstack/react-table";
import { format } from "date-fns";
import {
  EllipsisVertical,
  Package,
  ShoppingCart,
  Plus,
  Minus,
} from "lucide-react";
import { useCallback, useEffect, useState, useMemo } from "react";
import { useAppDispatch } from "@/store/hooks";
import { generateImageSrc } from "@/utils/helper";
import { formatCurrency } from "@/utils/currency";
import {
  attachWholesalerProductRequest,
  detachWholesalerProductRequest,
  removeSelectedProductIds,
  setSelectedProductIds,
} from "@/store/slices/wholesalerProducts.slice";

const getStatusColor = (status: string) => {
  switch (status) {
    case "ACTIVE":
      return "bg-green-500/20 text-green-600 hover:bg-green-500/20";
    case "DRAFT":
      return "bg-orange-500/20 text-orange-600 hover:bg-orange-500/20";
    case "INACTIVE":
      return "bg-red-500/20 text-red-600 hover:bg-red-500/20";
    default:
      return "bg-gray-500/20 text-gray-600 hover:bg-gray-500/20";
  }
};

const formatStatus = (status: string) => {
  switch (status) {
    case "ACTIVE":
      return "Active";
    case "DRAFT":
      return "Draft";
    case "INACTIVE":
      return "Inactive";
    default:
      return status.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  }
};

const getVariantDisplayName = (variant: any): string => {
  const options = [variant?.option1, variant?.option2, variant?.option3].filter(
    (opt) => opt && opt.trim() !== "",
  );
  if (options.length > 0) {
    return options.join(" / ");
  }
  return variant?.title || "Unknown";
};

const ProductThumbnail: React.FC<{ imageId?: string; title: string }> = ({
  imageId,
  title,
}) => {
  const [hasError, setHasError] = useState(false);

  if (!imageId || hasError) {
    return (
      <div className="w-10 h-10 bg-muted rounded-md flex items-center justify-center">
        <Package className="h-4 w-4 text-muted-foreground" />
      </div>
    );
  }

  return (
    <img
      src={imageId}
      alt={title}
      className="w-10 h-10 object-cover"
      onError={() => setHasError(true)}
    />
  );
};

const resolveTableImageUrl = (rawValue?: string): string => {
  if (!rawValue) return "";
  const value = rawValue.trim();
  if (!value) return "";

  if (/^https?:\/\//i.test(value)) {
    return value;
  }

  const normalized = value.startsWith("/") ? value.slice(1) : value;
  if (normalized.startsWith("store/")) {
    return generateImageSrc(normalized, 60, 60);
  }

  // Ignore bare IDs (UUIDs) to avoid invalid CDN requests like /<uuid>.
  return "";
};

export const columns: ColumnDef<Product>[] = [
  {
    id: "select",
    size: 50,
    minSize: 40,
    maxSize: 60,
    header: ({ table }) => {
      const { rows } = table.getRowModel();
      const selectedRows = table.getSelectedRowModel().rows;
      const isAllSelected =
        rows.length > 0 && selectedRows.length === rows.length;
      const isSomeSelected = selectedRows.length > 0 && !isAllSelected;

      return (
        <div
          className="flex items-center justify-center transition-opacity duration-200"
          onClick={(e) => e.stopPropagation()}
        >
          <Checkbox
            checked={
              isAllSelected ? true : isSomeSelected ? "indeterminate" : false
            }
            onCheckedChange={(value) => {
              if (value) table.toggleAllPageRowsSelected(true);
              else table.toggleAllPageRowsSelected(false);
            }}
            aria-label="Select all"
            className="!bg-transparent border-foreground text-foreground data-[state=checked]:text-foreground"
          />
        </div>
      );
    },
    cell: ({ row }) => <SelectProduct row={row} />,
  },
  {
    accessorKey: "title",
    size: 280,
    minSize: 220,
    maxSize: 350,
    header: ({ column }) => (
      <Button
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        variant="ghost"
        className="w-full flex p-0 items-center justify-between hover:!bg-transparent hover:text-inherit group"
      >
        <span className="text-base font-bold">Product</span>
        <div
          className={`transition-opacity duration-200 ${column.getIsSorted() ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}
        >
          <TableSortingIcon sortDirection={column.getIsSorted()} />
        </div>
      </Button>
    ),
    cell: ({ row }) => {
      const product = row.original;
      const date = product.updated_at
        ? new Date(product.updated_at)
        : new Date();
      const formattedDate = format(date, "MMM dd yyyy");
      const assetPath =
        (Array.isArray((product as any)?.assets)
          ? (product as any).assets.find((a: any) => a?.asset?.path)?.asset
              ?.path
          : undefined) || (product as any)?.image_asset?.path;
      const rawProductImage =
        assetPath ||
        (Array.isArray(product.images) && product.images.length > 0
          ? product.images[0]
          : undefined) ||
        (product as any).image ||
        (product as any).imageAssetId;
      const productImage = resolveTableImageUrl(rawProductImage);

      return (
        <div className="flex gap-3 items-center">
          <div className="w-10 h-10 overflow-hidden object-cover rounded-md flex-shrink-0">
            <ProductThumbnail imageId={productImage} title={product.title} />
          </div>
          <div className="min-w-0 flex-1">
            <h3
              className="text-sm font-semibold truncate max-w-[200px]"
              title={product.title}
            >
              {product.title}
            </h3>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-xs text-muted-foreground">
                {formattedDate}
              </span>
              <span className="text-xs text-muted-foreground">•</span>
              <span className="text-xs text-muted-foreground font-mono">
                {product.sku || "No SKU"}
              </span>
            </div>
          </div>
        </div>
      );
    },
  },
  {
    accessorKey: "price_range",
    size: 120,
    minSize: 100,
    maxSize: 150,
    header: ({ column }) => (
      <div className="group">
        <Button
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          variant="ghost"
          className="w-full flex p-0 items-center justify-between hover:!bg-transparent hover:text-inherit"
        >
          <span className="text-base font-bold">Price</span>
          <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            <TableSortingIcon sortDirection={column.getIsSorted()} />
          </div>
        </Button>
      </div>
    ),
    cell: ({ row }) => {
      const variants = row.original.variants;
      if (!variants || variants.length === 0) {
        return (
          <div>
            <h3 className="text-xs font-semibold">No variants</h3>
          </div>
        );
      }

      const prices = variants
        .map((v) => {
          const vv = v as VariantApiShape | any;
          if (typeof vv.price === "number") return vv.price;
          if (typeof vv.listing_price === "number") return vv.listing_price;
          if (typeof vv.listingPrice === "number") return vv.listingPrice;
          return 0;
        })
        .filter((p) => typeof p === "number");
      const minPrice = prices.length > 0 ? Math.min(...prices) : 0;
      const maxPrice = prices.length > 0 ? Math.max(...prices) : 0;

      return (
        <div>
          <h3 className="text-xs font-semibold">
            {minPrice === maxPrice
              ? formatCurrency(minPrice)
              : `${formatCurrency(minPrice)} - ${formatCurrency(maxPrice)}`}
          </h3>
          {variants.length > 1 && (
            <span className="text-xs text-gray-600">
              {variants.length} variants
            </span>
          )}
        </div>
      );
    },
  },
  {
    accessorKey: "variants",
    size: 200,
    minSize: 150,
    maxSize: 300,
    header: () => <span className="text-base font-bold">Variants</span>,
    cell: ({ row }) => {
      const variants = row.original.variants;

      if (!variants || variants.length === 0) {
        return (
          <div>
            <h3 className="text-xs font-semibold">No variants</h3>
            <span className="text-xs text-gray-600">0 options</span>
          </div>
        );
      }

      const totalInventory = variants.reduce(
        (sum, variant) =>
          sum +
          ((variant as any).inventory ??
            (variant as any).inventory_quantity ??
            0),
        0,
      );
      const variantCount = variants.length;
      const variantTitles = variants
        .slice(0, 2)
        .map((variant) => getVariantDisplayName(variant))
        .join(", ");
      const hasMore = variants.length > 2;

      const triggerContent = (
        <div>
          <h3
            className="text-xs font-semibold truncate max-w-[150px]"
            title={variantTitles}
          >
            {variantTitles}
            {hasMore ? ` +${variants.length - 2} more` : ""}
          </h3>
          <span className="text-xs text-gray-600">
            {totalInventory} in stock • {variantCount} variant
            {variantCount !== 1 ? "s" : ""}
          </span>
        </div>
      );

      return (
        <ProductVariantsPopover variants={variants} trigger={triggerContent} />
      );
    },
  },
  {
    accessorKey: "category",
    size: 150,
    minSize: 120,
    maxSize: 200,
    header: ({ column }) => (
      <div className="group">
        <Button
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          variant="ghost"
          className="w-full flex p-0 items-center justify-between hover:!bg-transparent hover:text-inherit"
        >
          <span className="text-base font-bold">Collection</span>
          <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            <TableSortingIcon sortDirection={column.getIsSorted()} />
          </div>
        </Button>
      </div>
    ),
    cell: ({ row }) => {
      const category =
        row.original.metafields?.find(
          (item: Metafield) => item.key === "category",
        )?.value || "Uncategorized";
      return (
        <div>
          <h3 className="text-xs font-semibold truncate capitalize">
            {category}
          </h3>
        </div>
      );
    },
  },
  {
    accessorKey: "status",
    size: 120,
    minSize: 100,
    maxSize: 160,
    header: () => <span className="text-base font-bold">Status</span>,
    cell: ({ row }) => {
      const status = row.original.status || "INACTIVE";
      return (
        <Badge
          variant="secondary"
          className={`rounded-full px-[10px] py-[5px] ${getStatusColor(status)}`}
        >
          {formatStatus(status)}
        </Badge>
      );
    },
  },
  {
    accessorKey: "actions",
    size: 200,
    minSize: 150,
    maxSize: 250,
    header: () => <span className="text-base font-bold">Actions</span>,
    cell: ({ row }) => <ProductActionsCell product={row.original} />,
  },
];

const SelectProduct: React.FC<{ row: any }> = ({ row }) => {
  const dispatch = useAppDispatch();
  const isSelected = row.getIsSelected();

  const handleSelect = useCallback(
    (value: boolean) => {
      if (!row.original.id) return;
      if (value) dispatch(setSelectedProductIds([row.original]));
      else dispatch(removeSelectedProductIds([row.original.id]));
    },
    [dispatch, row.original],
  );

  useEffect(() => {
    if (isSelected) handleSelect(true);
    else handleSelect(false);
  }, [isSelected, handleSelect]);

  return (
    <div
      className={`flex items-center justify-center transition-opacity duration-200 ${isSelected ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}
      onClick={(e) => e.stopPropagation()}
    >
      <Checkbox
        checked={isSelected}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label="Select row"
        className="!bg-transparent border-foreground text-foreground data-[state=checked]:text-foreground"
      />
    </div>
  );
};

const ProductActionsCell: React.FC<{ product: Product }> = ({ product }) => {
  const dispatch = useAppDispatch();
  const [type, setType] = useState<string>("percentage");
  const [paymentType, setPaymentType] = useState<string[]>(["PREPAID"]);
  const [value, setValue] = useState<number>(0);
  const [removedVariants, _setRemovedVariants] = useState<Set<string>>(
    new Set(),
  );

  // Calculate new price based on margin (applied to all variants of the product)
  // Margin is in rupees, prices are in paise, so multiply fixed margin by 100
  const calculateNewPrice = (
    originalPrice: number,
    margin: number,
    marginType: string,
  ) => {
    if (marginType === "percentage") {
      return originalPrice + (originalPrice * margin) / 100;
    } else {
      return originalPrice + margin * 100;
    }
  };

  // Calculate profit
  const calculateProfit = (originalPrice: number, newPrice: number) => {
    return newPrice - originalPrice;
  };

  // Get variants or create a default variant
  const variants = useMemo(() => {
    if (product.variants && product.variants.length > 0) {
      return product.variants;
    }
    // If no variants, create a default one with the product's base price
    return [
      {
        id: product.id,
        title: "Default",
        price: product.price || 0,
        sku: product.sku || "",
        inventory_quantity: 0,
      },
    ];
  }, [product]);

  // Calculate total profit across all non-removed variants
  const totalProfit = useMemo(() => {
    return variants
      .filter((v) => !removedVariants.has(v.id || ""))
      .reduce((sum, variant) => {
        const originalPrice = variant.price || 0;
        const newPrice = calculateNewPrice(originalPrice, value, type);
        return sum + calculateProfit(originalPrice, newPrice);
      }, 0);
  }, [variants, removedVariants, value, type]);

  const handleAction = (remove: boolean) => {
    if (!product.owner_id || !product.id) return;
    if (remove) {
      dispatch(
        detachWholesalerProductRequest({
          id: product.owner_id,
          product_ids: [product.id],
        }),
      );
    } else {
      dispatch(
        attachWholesalerProductRequest({
          id: product.owner_id,
          margin: value,
          margin_type: type,
          payment_type: paymentType,
          product_ids: [product.id],
        }),
      );
    }
  };

  return (
    <div className="flex items-center gap-2">
      {product.already_mapped ? (
        <Button
          variant="destructive"
          size="sm"
          className="h-8 px-3 text-xs bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-500/30"
          onClick={() => handleAction(true)}
        >
          <Minus className="h-3 w-3 mr-1" /> Remove
        </Button>
      ) : (
        <Dialog>
          <DialogTrigger asChild>
            <Button
              variant="default"
              size="sm"
              className="h-8 px-3 text-xs bg-primary text-primary-foreground hover:bg-primary/90"
            >
              <Plus className="h-3 w-3 mr-1" /> Add
            </Button>
          </DialogTrigger>
          <DialogContent className="p-0 overflow-hidden max-w-[1000px] bg-card max-h-[95vh] flex flex-col">
            <DialogHeader className="bg-gradient-to-r from-primary to-primary/80 px-6 py-5 flex-shrink-0">
              <DialogTitle className="text-white text-xl font-semibold">
                Import Product to Inventory
              </DialogTitle>
              <p className="text-white/80 text-sm mt-1">
                Configure pricing and payment options for product variants
              </p>
            </DialogHeader>

            <div className="flex-1 overflow-y-auto min-h-0">
              <div className="p-6 space-y-6">
                {/* Product Margin Settings */}
                <div className="bg-muted/30 rounded-lg p-4 border border-border/50">
                  <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                    <span className="h-1.5 w-1.5 rounded-full bg-primary"></span>
                    Product Margin & Payment Settings
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label
                        htmlFor="margin"
                        className="text-xs font-medium text-muted-foreground"
                      >
                        Margin Value (Applied to all variants)
                      </Label>
                      <div className="flex gap-2">
                        <InputPrimary
                          value={value}
                          onChange={(e) => setValue(Number(e.target.value))}
                          type="number"
                          className="!h-11 flex-1"
                          placeholder="Enter margin"
                        />
                        <Select value={type} onValueChange={setType}>
                          <SelectTrigger className="!bg-primary rounded-lg text-white !h-11 w-24 border-0">
                            <SelectValue placeholder="Type" />
                          </SelectTrigger>
                          <SelectContent className="bg-card">
                            <SelectItem value="percentage">%</SelectItem>
                            <SelectItem value="fixed">₹</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-xs font-medium text-muted-foreground">
                        Payment Methods
                      </Label>
                      <div className="flex flex-wrap gap-3 pt-1">
                        {[
                          { id: "PREPAID", label: "Prepaid" },
                          { id: "SEMIPREPAID", label: "Semi Prepaid" },
                          { id: "POSTPAID", label: "COD" },
                        ].map((method) => (
                          <div
                            key={method.id}
                            className="flex items-center gap-2"
                          >
                            <Checkbox
                              id={method.id.toLowerCase()}
                              checked={paymentType.includes(method.id)}
                              onCheckedChange={(checked) =>
                                setPaymentType((prev) =>
                                  checked
                                    ? [...prev, method.id]
                                    : prev.filter((item) => item !== method.id),
                                )
                              }
                              className="w-4 h-4 rounded-md data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                            />
                            <Label
                              htmlFor={method.id.toLowerCase()}
                              className="text-sm cursor-pointer"
                            >
                              {method.label}
                            </Label>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Product Summary Card */}
                <div className="bg-gradient-to-br from-primary/10 to-primary/5 rounded-lg p-4 border border-primary/20">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 rounded-lg overflow-hidden bg-muted">
                        {product.images && product.images.length > 0 ? (
                          <img
                            src={getAsset(product.images[0], {
                              width: 120,
                              height: 120,
                              fit: "cover",
                            })}
                            alt={product.title}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Package className="h-8 w-8 text-muted-foreground" />
                          </div>
                        )}
                      </div>
                      <div>
                        <h4 className="font-semibold text-base">
                          {product.title}
                        </h4>
                        <p className="text-sm text-muted-foreground mt-1">
                          {
                            variants.filter(
                              (v) => !removedVariants.has(v.id || ""),
                            ).length
                          }{" "}
                          variant(s) • Total Profit:{" "}
                          <span className="font-semibold text-green-600">
                            {formatPaise(totalProfit)}
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Product Variants Table */}
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold flex items-center gap-2">
                    <span className="h-1.5 w-1.5 rounded-full bg-primary"></span>
                    Product Variants
                  </h3>

                  <Accordion
                    type="single"
                    collapsible
                    defaultValue="product-accordion"
                    className="w-full"
                  >
                    <AccordionItem
                      value="product-accordion"
                      className="border border-border/50 rounded-lg overflow-hidden bg-background"
                    >
                      <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-muted/30 transition-colors data-[state=open]:bg-muted/50">
                        <div className="flex items-center justify-between w-full pr-2">
                          <div className="flex items-center gap-3">
                            <Package className="h-5 w-5 text-primary" />
                            <div className="text-left">
                              <h4 className="font-semibold text-sm">
                                {product.title}
                              </h4>
                              <p className="text-xs text-muted-foreground mt-0.5">
                                {variants.length} variant(s) available
                              </p>
                            </div>
                          </div>
                          <div className="text-xs text-muted-foreground">
                            Click to view variants
                          </div>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="p-0">
                        <div className="bg-background">
                          <Table>
                            <TableHeader>
                              <TableRow className="bg-muted/50 hover:bg-muted/50 border-t">
                                <TableHead className="font-semibold w-[80px]">
                                  Image
                                </TableHead>
                                <TableHead className="font-semibold">
                                  Name
                                </TableHead>
                                <TableHead className="font-semibold w-[140px]">
                                  Original Price
                                </TableHead>
                                <TableHead className="font-semibold w-[140px]">
                                  New Price
                                </TableHead>
                                <TableHead className="font-semibold w-[140px]">
                                  Total Profit
                                </TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {variants.map((variant, index) => {
                                const variantId =
                                  variant.id || `variant-${index}`;
                                const isRemoved =
                                  removedVariants.has(variantId);
                                const originalPrice = variant.price || 0;
                                const newPrice = calculateNewPrice(
                                  originalPrice,
                                  value,
                                  type,
                                );
                                const profit = calculateProfit(
                                  originalPrice,
                                  newPrice,
                                );

                                return (
                                  <TableRow
                                    key={variantId}
                                    className={`hover:bg-muted/30 ${isRemoved ? "opacity-50 bg-muted/20" : ""}`}
                                  >
                                    <TableCell className="py-3">
                                      <div className="w-14 h-14 rounded-md overflow-hidden bg-muted">
                                        {product.images &&
                                        product.images.length > 0 ? (
                                          <img
                                            src={getAsset(product.images[0], {
                                              width: 100,
                                              height: 100,
                                              fit: "cover",
                                            })}
                                            alt={variant.title}
                                            className="w-full h-full object-cover"
                                          />
                                        ) : (
                                          <div className="w-full h-full flex items-center justify-center">
                                            <Package className="h-6 w-6 text-muted-foreground" />
                                          </div>
                                        )}
                                      </div>
                                    </TableCell>
                                    <TableCell className="py-3">
                                      <div>
                                        <p className="font-medium text-sm">
                                          {variant.title}
                                        </p>
                                        <p className="text-xs text-muted-foreground mt-1">
                                          SKU: {variant.sku || "N/A"}
                                        </p>
                                      </div>
                                    </TableCell>
                                    <TableCell className="py-3">
                                      <span className="font-semibold text-sm">
                                        {formatPaise(originalPrice)}
                                      </span>
                                    </TableCell>
                                    <TableCell className="py-3">
                                      <span className="font-semibold text-sm text-primary">
                                        {formatPaise(newPrice)}
                                      </span>
                                    </TableCell>
                                    <TableCell className="py-3">
                                      <span className="font-semibold text-sm text-green-600">
                                        {formatPaise(profit)}
                                      </span>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </div>
              </div>
            </div>

            <DialogFooter className="justify-between p-6 pt-4 border-t border-border/50 flex-shrink-0 bg-muted/20 flex flex-row gap-3">
              <div className="text-sm text-muted-foreground">
                {
                  variants.filter((v) => !removedVariants.has(v.id || ""))
                    .length
                }{" "}
                variant(s) will be imported
              </div>
              <div className="flex gap-3">
                <DialogTrigger asChild>
                  <ButtonOutline
                    type="button"
                    className="px-6 rounded-lg border-2 hover:bg-muted"
                  >
                    Cancel
                  </ButtonOutline>
                </DialogTrigger>
                <ButtonPrimary
                  type="button"
                  className="px-6 rounded-lg"
                  onClick={() => handleAction(false)}
                >
                  Import Product
                </ButtonPrimary>
              </div>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
            <EllipsisVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="bg-card dark:bg-card text-foreground"
          align="end"
        >
          <DropdownMenuLabel>Actions</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem>
            <Package className="h-4 w-4 mr-2" />
            View Details
          </DropdownMenuItem>
          <DropdownMenuItem>
            <ShoppingCart className="h-4 w-4 mr-2" />
            View Variants
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
